// TESSERAX — MÉTRIQUES DE MOTIVATION (MOT-METRICS-002). Module PUR : ni THREE ni DOM,
// pas de Math.random, pas d'I/O. main.ts/net gèrent la persistance & le réseau.
//
// Deux usages, une seule source :
//   1. AGRÉGAT LOCAL (toujours actif, privé, ne quitte JAMAIS l'appareil) — un registre
//      { metric: compte } en localStorage. Donne une baseline mesurable sur l'appareil
//      (dump JSON) sans aucune dépendance réseau ni consentement.
//   2. TÉLÉMÉTRIE OPT-IN (best-effort) — quand le joueur l'active, on POST le DELTA des
//      compteurs vers /api/telemetry (compteurs agrégés D1, anonymes). L'allowlist ci-dessous
//      est PARTAGÉE avec la fonction serveur (validation bit-identique, comme core/verify).
//
// Aucun de ces compteurs n'influence le gameplay ni la RNG → déterminisme du défi intact.
import { type NumRegistry, parseNumReg, serializeNumReg } from './numreg';

// ALLOWLIST des évènements de funnel suivis. Croît par AJOUT (le serveur rejette tout
// ce qui n'est pas ici → pas de pollution de la table de télémétrie).
export const METRIC_KEYS = [
  'run',          // toute partie terminée (hors tutoriel/debug)
  'run_first',    // la TOUTE PREMIÈRE partie du joueur (onboarding)
  'session',      // 1ʳᵉ partie d'une session de page (rétention intra-session)
  'defeat',       // partie terminée par défaite (top-out / temps écoulé)
  'victory',      // partie terminée par OBJECTIF atteint (épreuve / strate / mode court)
  'retry_fail',   // « Rejouer » cliqué après une défaite (boucle de retry)
  'retry_win',    // « Rejouer » cliqué après une victoire (relance post-réussite)
  'hub_open',     // ouverture du hub de progression
  'codex_open',   // ouverture de la collection de reliques
  'shop_open',    // ouverture de la boutique
  'relic_unlock', // reliques nouvellement débloquées (compte)
  'ach_unlock',   // succès nouvellement débloqués (compte)
  // — Funnel BOUCLE DE JEU (RET-METRICS-FUNNEL-001) —
  'tutorial_start',    // tutoriel lancé
  'tutorial_done',     // tutoriel mené jusqu'au bout
  'tutorial_skip',     // tutoriel quitté avant la fin
  'first_clear',       // TOUT PREMIER effacement de ligne du joueur (onboarding)
  'menu_quit',         // « Menu » cliqué sur l'écran de fin (au lieu de Rejouer)
  'daily_play',        // une partie du défi du jour terminée
  'return_day',        // 1ʳᵉ session d'un nouveau jour calendaire (proxy rétention J1/J7)
  'pli_unlock',        // le Pli/4D vient d'être débloqué (jalon d'onboarding moyen terme)
  // — ADOPTION des mécaniques signature (MOT-METRICS-MECHANIC-001) — comptées UNE fois/run
  //   (et non par usage) → chaque compteur se lit directement en % des runs (÷ 'run'). Répond à
  //   la question « les mécaniques d'identité atterrissent-elles ? » : 'fold_run' → 'tessera_run'
  //   est l'entonnoir de conversion du Pli (utilisé vs. payé), 'bascule_run' l'adoption du tilt.
  'bascule_run',       // run où la Bascule a été utilisée ≥1×
  'fold_run',          // run où le Pli (4D) a été déclenché ≥1×
  'zone_run',          // run où le pouvoir Zone a été activé ≥1×
  'tessera_run',       // run où ≥1 TESSERAX (feat 4D, payoff du Pli) a été réalisé
  'croix_run',         // run où ≥1 CROIX (les deux axes d'un coup — le feat 3D) a été réalisée
  // — PROFONDEUR atteinte (paliers, comptés UNE fois/run au franchissement) : depuis les seuls
  //   compteurs agrégés on reconstruit la courbe de progression/difficulté ('run'→l5→l10→l15 =
  //   où meurent les runs), sans aucune donnée par-joueur.
  'reach_l5', 'reach_l10', 'reach_l15',
] as const;
export type MetricKey = typeof METRIC_KEYS[number];

const ALLOWED = new Set<string>(METRIC_KEYS as readonly string[]);
export function isMetricKey(k: string): boolean { return ALLOWED.has(k); }

export type MetricRegistry = NumRegistry;
export function parseMetrics(raw: string | null | undefined): MetricRegistry { return parseNumReg(raw); }
export function serializeMetrics(m: MetricRegistry): string { return serializeNumReg(m); }

// Borne d'un incrément (anti-abus / saisie folle). Un évènement compte au plus MAX_BUMP.
export const MAX_BUMP = 100000;

// Incrémente un compteur. PUR/immuable : renvoie un registre NEUF (ou le même si la clé est
// inconnue / l'incrément invalide). `n` est borné à [1, MAX_BUMP] et arrondi.
export function bumpMetric(reg: MetricRegistry, key: string, n = 1): MetricRegistry {
  if (!ALLOWED.has(key)) return reg;
  const inc = Math.min(MAX_BUMP, Math.max(1, Math.floor(Number.isFinite(n) ? n : 1)));
  return { ...reg, [key]: (reg[key] || 0) + inc };
}

// DELTA positif entre l'état courant et le dernier état ENVOYÉ (pour n'expédier que le neuf).
// Ne renvoie que des clés autorisées dont le compte a augmenté. PUR.
export function metricsDelta(cur: MetricRegistry, sent: MetricRegistry): MetricRegistry {
  const out: MetricRegistry = {};
  for (const k of METRIC_KEYS) {
    const d = (cur[k] || 0) - (sent[k] || 0);
    if (d > 0) out[k] = d;
  }
  return out;
}

// Sanitize d'un payload réseau ENTRANT (serveur) : ne garde que les clés autorisées et borne
// chaque compte à [1, MAX_BUMP]. Renvoie {} si rien de valide. PUR (réutilisé par la Function).
export function sanitizeIncoming(ev: unknown): MetricRegistry {
  const out: MetricRegistry = {};
  if (!ev || typeof ev !== 'object') return out;
  for (const k of METRIC_KEYS) {
    const raw = (ev as Record<string, unknown>)[k];
    const v = Math.floor(Number(raw));
    if (Number.isFinite(v) && v >= 1) out[k] = Math.min(MAX_BUMP, v);
  }
  return out;
}
